import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const EVENT_COLORS = {
  call: '#3a6ad9',
  meeting: '#3a6ad9',
  closing: '#acf44e',
  appraisal: '#d97706',
  inspection: '#ff082f',
  other: '#6b7280',
};

const BLANK_EVENT = { title: '', description: '', event_type: 'call', start_time: '', end_time: '', location: '' };

// Acquisition-related date labels — property/contract dates
const ACQUISITION_LABELS = ['Closing Date', 'EMD Due', 'Option Period End', 'Inspection', 'Appraisal', 'Final Walkthrough', 'Consummation'];


export default function AdminSchedule() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') || 'all';
  const token = localStorage.getItem('doorly_admin_token');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form, setForm] = useState(BLANK_EVENT);
  const [saving, setSaving] = useState(false);
  const [loanDates, setLoanDates] = useState([]);
  const [meetings, setMeetings] = useState([]);

  const fetchLoanDates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/admin/schedule/loan-dates`, { headers: { Authorization: `Bearer ${token}` } });
      setLoanDates(res.data.events || []);
    } catch (_) {}
  }, [token]);

  const fetchEvents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month: `${year}-${String(month + 1).padStart(2, '0')}` },
      });
      setEvents(res.data || []);
    } catch {}
    setLoading(false);
  }, [token, year, month]);

  const fetchMeetings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/admin/partners/meetings`, { headers: { Authorization: `Bearer ${token}` } });
      setMeetings(res.data.meetings || []);
    } catch (_) { setMeetings([]); }
  }, [token]);

  useEffect(() => { fetchEvents(); fetchLoanDates(); fetchMeetings(); }, [fetchEvents, fetchLoanDates, fetchMeetings]);

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDay = (y, m) => new Date(y, m, 1).getDay();

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDay(year, month);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const calendarEvents = events.filter((e) => e.start_time?.startsWith(dateStr)).map(e => ({ ...e, _source: 'calendar' }));

    const loanDateEvents = loanDates.filter(e => e.date === dateStr).map(e => ({
      id: `ld-${e.borrower_id}-${e.label}`, title: `${e.icon} ${e.label}`, start_time: dateStr,
      event_type: ACQUISITION_LABELS.includes(e.label) ? 'acquisition' : 'loan_date',
      borrower_name: e.borrower_name, borrower_id: e.borrower_id,
      _source: ACQUISITION_LABELS.includes(e.label) ? 'acquisition' : 'loan_date',
      _color: e.color,
    }));

    const meetingEvents = meetings.filter(m => (m.start_time || m.datetime || '').startsWith(dateStr)).map(m => ({
      id: `mt-${m.id}`, title: `${m.partner_name || m.invitee_name || 'Meeting'} — ${m.event_name || 'Partner Call'}`,
      start_time: m.start_time || m.datetime,
      event_type: 'meeting', _source: 'meeting', _color: '#f5b832',
      description: m.event_name || '',
      borrower_name: m.partner_name || m.invitee_name,
    }));

    let all = [...calendarEvents, ...loanDateEvents, ...meetingEvents];
    if (view === 'loan_dates') all = all.filter(e => e._source === 'loan_date');
    else if (view === 'acquisition') all = all.filter(e => e._source === 'acquisition');
    else if (view === 'meetings') all = all.filter(e => e._source === 'meeting');
    return all;
  };

  const prevMonth = () => {
    if (month === 0) { setYear((y) => y - 1); setMonth(11); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear((y) => y + 1); setMonth(0); }
    else setMonth((m) => m + 1);
  };

  const openAddForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setForm({ ...BLANK_EVENT, start_time: `${dateStr}T09:00`, end_time: `${dateStr}T10:00` });
    setSelectedDay(day);
    setSelectedEvent(null);
    setShowModal(true);
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/schedule`, form, { headers: { Authorization: `Bearer ${token}` } });
      setShowModal(false);
      setForm(BLANK_EVENT);
      fetchEvents();
    } catch {}
    setSaving(false);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/api/admin/schedule/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedEvent(null);
      fetchEvents();
    } catch {}
  };

  const today = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  // Upcoming events (next 30 days) — all sources, filtered by view
  const upcomingDateStr = new Date().toISOString().split('T')[0];
  const allUpcoming = [
    ...events.filter(e => e.start_time >= upcomingDateStr).map(e => ({ ...e, _source: 'calendar' })),
    ...loanDates.filter(e => e.date >= upcomingDateStr).map(e => ({
      id: `ld-${e.borrower_id}-${e.label}`, title: `${e.icon} ${e.label}`, start_time: e.date,
      borrower_name: e.borrower_name, borrower_id: e.borrower_id,
      _source: ACQUISITION_LABELS.includes(e.label) ? 'acquisition' : 'loan_date',
      event_type: ACQUISITION_LABELS.includes(e.label) ? 'acquisition' : 'loan_date', _color: e.color,
    })),
    ...meetings.filter(m => (m.start_time || m.datetime || '') >= upcomingDateStr).map(m => ({
      id: `mt-${m.id}`, title: `${m.partner_name || m.invitee_name || 'Meeting'} — ${m.event_name || 'Partner Call'}`,
      start_time: m.start_time || m.datetime,
      _source: 'meeting', event_type: 'meeting', _color: '#f5b832',
      borrower_name: m.partner_name || m.invitee_name,
    })),
  ];
  let upcoming = allUpcoming;
  if (view === 'loan_dates') upcoming = upcoming.filter(e => e._source === 'loan_date');
  else if (view === 'acquisition') upcoming = upcoming.filter(e => e._source === 'acquisition');
  else if (view === 'meetings') upcoming = upcoming.filter(e => e._source === 'meeting');
  upcoming = upcoming.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).slice(0, 12);

  return (
    <AdminLayout>
      <div style={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Calendar Main */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={prevMonth} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', minWidth: '180px', textAlign: 'center' }}>{MONTHS[month]} {year}</h2>
              <button onClick={nextMonth} style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
            </div>
            <button onClick={() => { setForm(BLANK_EVENT); setSelectedDay(null); setShowModal(true); }}
              style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              + Add Event
            </button>
          </div>

          {/* Calendar Grid */}
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #f3f4f6', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            {/* Day Headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #f3f4f6' }}>
              {DAYS.map((d) => (
                <div key={d} style={{ padding: '10px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{d}</div>
              ))}
            </div>

            {/* Calendar Days */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {/* Empty cells before first day */}
              {Array.from({ length: firstDay }, (_, i) => (
                <div key={`empty-${i}`} style={{ minHeight: '100px', borderRight: '1px solid #f9fafb', borderBottom: '1px solid #f9fafb', backgroundColor: '#fafafa' }} />
              ))}

              {/* Day cells */}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dayEvents = getEventsForDay(day);
                const isToday = isCurrentMonth && day === today;
                return (
                  <div key={day}
                    onClick={() => openAddForDay(day)}
                    style={{
                      minHeight: '100px', padding: '6px', cursor: 'pointer',
                      borderRight: '1px solid #f9fafb', borderBottom: '1px solid #f9fafb',
                      backgroundColor: isToday ? '#eff4ff' : '#fff',
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isToday) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                    onMouseLeave={(e) => { if (!isToday) e.currentTarget.style.backgroundColor = '#fff'; }}
                  >
                    <div style={{ fontSize: '12px', fontWeight: isToday ? '700' : '500', color: isToday ? '#fff' : '#374151', marginBottom: '4px', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '9999px', backgroundColor: isToday ? '#3a6ad9' : 'transparent' }}>
                      {day}
                    </div>
                    {dayEvents.slice(0, 3).map((ev) => (
                      <div key={ev.id}
                        onClick={(e) => { e.stopPropagation(); if (ev.borrower_id) navigate(`/admin/borrowers/${ev.borrower_id}`); else setSelectedEvent(ev); }}
                        style={{ fontSize: '10px', fontWeight: '600', padding: '2px 6px', borderRadius: '4px', marginBottom: '2px', backgroundColor: ev._color || EVENT_COLORS[ev.event_type] || '#6b7280', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                        {ev.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div style={{ fontSize: '10px', color: '#9ca3af', paddingLeft: '4px' }}>+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Upcoming Sidebar */}
        <div style={{ width: '260px', flexShrink: 0, backgroundColor: '#fff', borderLeft: '1px solid #e5e7eb', overflowY: 'auto', padding: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '16px' }}>
            {view === 'loan_dates' ? 'Upcoming Loan Dates' : view === 'acquisition' ? 'Upcoming Acquisition Dates' : view === 'meetings' ? 'Upcoming Partner Meetings' : 'Upcoming Events'}
          </h3>
          {loading && <p style={{ fontSize: '13px', color: '#9ca3af' }}>Loading…</p>}
          {!loading && upcoming.length === 0 && <p style={{ fontSize: '13px', color: '#9ca3af' }}>No upcoming events.</p>}
          {upcoming.map((ev) => (
            <div key={ev.id}
              onClick={() => setSelectedEvent(ev)}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #f3f4f6', marginBottom: '8px', cursor: 'pointer', borderLeft: `3px solid ${EVENT_COLORS[ev.event_type] || '#6b7280'}` }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
            >
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>{ev.title}</div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                {ev.start_time ? new Date(ev.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
              </div>
              {ev.borrower_name && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{ev.borrower_name}</div>}
            </div>
          ))}

          {/* Key Loan Dates */}
          {loanDates.length > 0 && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0 16px' }} />
              <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>📅 Key Loan Dates</h3>
              {loanDates.filter(e => {
                const d = new Date(e.date + 'T12:00:00');
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() + 30);
                return d >= today && d <= cutoff;
              }).slice(0, 10).map((e, i) => {
                const days = Math.ceil((new Date(e.date + 'T12:00:00') - new Date().setHours(0, 0, 0, 0)) / 86400000);
                return (
                  <div key={i}
                    onClick={() => navigate(`/admin/borrowers/${e.borrower_id}`)}
                    style={{
                      padding: '8px 10px', borderRadius: '8px', marginBottom: '6px', cursor: 'pointer',
                      border: `1px solid ${days <= (e.urgent_days || 3) ? '#fed7aa' : '#f3f4f6'}`,
                      background: days <= (e.urgent_days || 3) ? '#fff7ed' : '#fff',
                      borderLeft: `3px solid ${e.color || '#6b7280'}`,
                    }}
                    onMouseEnter={ev => ev.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={ev => ev.currentTarget.style.opacity = '1'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#111827' }}>{e.icon} {e.label}</span>
                      <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '8px', background: e.color, color: e.textColor }}>
                        {days === 0 ? 'Today' : days === 1 ? '1d' : `${days}d`}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      {e.borrower_name}{e.loan_number ? ` · ${e.loan_number}` : ''}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                );
              })}
              {loanDates.filter(e => { const d = new Date(e.date + 'T12:00:00'); const t = new Date(); t.setHours(0,0,0,0); return d < t; }).length > 0 && (
                <div style={{ marginTop: '8px', padding: '8px 10px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '8px', fontSize: '11px', color: '#ff082f', fontWeight: '600' }}>
                  ⚠️ {loanDates.filter(e => new Date(e.date + 'T12:00:00') < new Date().setHours(0,0,0,0)).length} overdue date(s)
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ height: '6px', borderRadius: '16px 16px 0 0', backgroundColor: EVENT_COLORS[selectedEvent.event_type] || '#6b7280' }} />
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'capitalize', marginBottom: '4px' }}>{selectedEvent.event_type}</div>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>{selectedEvent.title}</h3>
                </div>
                <button onClick={() => setSelectedEvent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '20px' }}>×</button>
              </div>
              {selectedEvent.description && <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>{selectedEvent.description}</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px', color: '#374151', marginBottom: '20px' }}>
                {selectedEvent.start_time && <div>🕐 {new Date(selectedEvent.start_time).toLocaleString()}</div>}
                {selectedEvent.end_time && <div>🕔 Ends {new Date(selectedEvent.end_time).toLocaleTimeString()}</div>}
                {selectedEvent.location && <div>📍 {selectedEvent.location}</div>}
                {selectedEvent.borrower_name && <div>👤 {selectedEvent.borrower_name}</div>}
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => deleteEvent(selectedEvent.id)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #ffb3bf', background: '#fff0f3', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#ff082f' }}>Delete</button>
                <button onClick={() => setSelectedEvent(null)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontWeight: '700', fontSize: '16px', color: '#111827' }}>New Event{selectedDay ? ` — ${MONTHS[month]} ${selectedDay}` : ''}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '20px' }}>×</button>
            </div>
            <form onSubmit={saveEvent} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Title *</label>
                <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Type</label>
                <select value={form.event_type} onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
                  style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none' }}>
                  {Object.keys(EVENT_COLORS).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Start *</label>
                  <input type="datetime-local" required value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>End</label>
                  <input type="datetime-local" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Location</label>
                <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Address or Zoom link"
                  style={{ width: '100%', height: '38px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Description</label>
                <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '4px' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#3a6ad9', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Create Event'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
